import React, { ReactNode } from 'react';
import { Box, Card, CardContent, Typography, Stack, useTheme } from '@mui/material';
import { keyframes } from '@mui/system';
import Markdown from 'markdown-to-jsx';
import { format } from 'date-fns';
import BlinkingText from '@/components/blinkingText';
import McpToolCallChip, { ToolCallItem } from '@/components/mcp/mcpToolCallChip';

const pulseKeyframe = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1.15);
  }
`;

type Props = {
  role: 'user' | 'system' | 'action-card';
  content?: string;
  timestamp?: Date;
  isStreaming?: boolean;
  toolCalls?: ToolCallItem[];
  status?: string;
  children?: ReactNode;
};

const ThinkingIndicator: React.FC<{ status?: string }> = ({ status }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.25, minHeight: 24 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          animation: `${pulseKeyframe} 1.4s infinite ease-in-out`,
          animationDelay: '0s',
        }}
      />
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          animation: `${pulseKeyframe} 1.4s infinite ease-in-out`,
          animationDelay: '0.2s',
        }}
      />
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          animation: `${pulseKeyframe} 1.4s infinite ease-in-out`,
          animationDelay: '0.4s',
        }}
      />
    </Box>
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
        fontSize: '0.8rem',
        fontStyle: 'italic',
        userSelect: 'none',
      }}
    >
      {status || 'Processing...'}
    </Typography>
  </Stack>
);

const MessageBubble: React.FC<Props> = ({ role, content, timestamp, isStreaming, toolCalls, status, children }) => {
  const theme = useTheme();

  if (role === 'action-card') {
    return (
      <Box sx={{ alignSelf: 'flex-start', maxWidth: '94%', my: 0.25 }}>
        {children}
      </Box>
    );
  }

  const isUser = role === 'user';
  const hasToolCalls = Boolean(toolCalls && toolCalls.length > 0);
  const hasContent = Boolean(content && content.length > 0);

  return (
    <Stack
      direction="column"
      spacing={0.25}
      sx={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        maxWidth: isUser ? '88%' : '94%',
        width: 'fit-content',
        my: 0.25,
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        justifyContent={isUser ? 'flex-end' : 'flex-start'}
        sx={{ px: 0.5 }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontSize: '0.68rem' }}>
          {isUser ? 'YOU' : 'ASSISTANT'}
        </Typography>
        {timestamp && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
            {format(timestamp, 'p')}
          </Typography>
        )}
      </Stack>

      <Card
        elevation={isUser ? 1 : 0}
        sx={{
          bgcolor: isUser ? theme.palette.primary.main : theme.palette.grey[100],
          color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
          borderRadius: 2,
          borderTopRightRadius: isUser ? 0 : 8,
          borderTopLeftRadius: isUser ? 8 : 0,
        }}
      >
        <CardContent sx={{ p: '8px 12px !important', '&:last-child': { pb: '8px !important' } }}>
          {hasToolCalls && (
            <Box sx={{ mb: hasContent || isStreaming ? 0.75 : 0, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {toolCalls!.map((tc, idx) => (
                <McpToolCallChip key={tc.id || idx} toolCall={tc} compact={!isUser} />
              ))}
            </Box>
          )}

          {isStreaming && !hasContent && (
            <ThinkingIndicator
              status={
                status && status !== 'Thinking...'
                  ? status
                  : (hasToolCalls
                    ? toolCalls?.some((t) => !t.result)
                      ? `Running ${toolCalls?.filter((t) => !t.result).map((t) => t.name).join(', ')}...`
                      : 'Processing tool results...'
                    : 'Processing...')
              }
            />
          )}

          {hasContent ? (
            <Typography
              variant="body2"
              component="div"
              sx={{ wordBreak: 'break-word', fontSize: '0.82rem', lineHeight: 1.45, '& p': { m: 0 } }}
            >
              <Markdown>{content || ''}</Markdown>
              {isStreaming && <BlinkingText>_</BlinkingText>}
            </Typography>
          ) : !isStreaming ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.78rem' }}>
              {hasToolCalls
                ? 'Tool execution completed. See results in the workspace panel.'
                : 'Response completed.'}
            </Typography>
          ) : null}

          {children && (
            <Box sx={{ mt: 1 }}>
              {children}
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default MessageBubble;

